<passkey-registration-prompt>
    <!-- Passkey Registration Prompt Modal -->
    <dialog id="passkey-prompt-modal" class="modal" ref="modal" aria-labelledby="passkey-prompt-modal-title">
        <div class="modal-box max-w-md">
            <h3 id="passkey-prompt-modal-title" class="font-bold text-lg mb-4">
                <i class="icon-metalark-shield text-primary me-2"></i>
                {locale?.t('passkeyprompt_title')}
            </h3>

            <p class="mb-4">
                {locale?.t('passkeyprompt_description')}
                <strong class="ms-1">{locale?.t('passkeyprompt_no_passwords')}</strong>
            </p>

            <!-- Benefits List -->
            <div class="bg-base-200 rounded-lg p-4 mb-4">
                <div class="space-y-2 text-sm">
                    <div class="flex items-start gap-2">
                        <i class="icon-metalark-check-circle text-success mt-0.5"></i>
                        <span>{locale?.t('passkeyprompt_benefit_biometrics')}</span>
                    </div>
                    <div class="flex items-start gap-2">
                        <i class="icon-metalark-check-circle text-success mt-0.5"></i>
                        <span>{locale?.t('passkeyprompt_benefit_devices')}</span>
                    </div>
                    <div class="flex items-start gap-2">
                        <i class="icon-metalark-check-circle text-success mt-0.5"></i>
                        <span>{locale?.t('passkeyprompt_benefit_secure')}</span>
                    </div>
                </div>
            </div>

            <!-- Info about smart naming -->
            <div class="bg-info/50 rounded-lg p-3 mb-4">
                <p class="text-sm text-info-content">
                    <i class="icon-metalark-info me-2"></i>
                    {locale?.t('passkeyprompt_auto_naming_info')}
                </p>
            </div>

            <!-- Error Alert -->
            <div if="{state.error}" class="alert alert-error mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm">{state.error}</span>
            </div>

            <div class="modal-action">
                <button
                    class="btn btn-ghost"
                    onclick="{handleMaybeLater}"
                    disabled="{state.loading}"
                >
                    {locale?.t('passkeyprompt_maybe_later')}
                </button>
                <button
                    class="btn btn-primary gap-2 {state.loading ? 'loading' : ''}"
                    onclick="{handleAddPasskey}"
                    disabled="{state.loading}"
                >
                    <span if="{!state.loading}">
                        <i class="icon-metalark-shield me-2"></i>
                        {locale?.t('passkeyprompt_add_passkey')}
                    </span>
                    <span if="{state.loading}">{locale?.t('passkeyprompt_adding')}</span>
                </button>
            </div>
        </div>
    </dialog>

    <script>
        import { getPasskeyManager, getAuthService } from '../../services/service-loader';
        import { ReturningUserManager } from '../../services/returning-user-manager.js';

        export default {
            includeTools: false,

            state: {
                loading: false,
                error: null,
                supported: false
            },

            async onMounted() {
                // Check if passkeys are supported
                const PasskeyManager = await getPasskeyManager()
                const support = await PasskeyManager.isSupported();
                this.update({ supported: support.supported });

                // Add close event listener to handle backdrop clicks and ESC key
                this.attachEventListener();

                // Check if we should show the prompt automatically
                if (this.props.autoShow && support.supported) {
                    this.show();
                }
            },

            attachEventListener() {
                const modal = this.refs?.modal || document.querySelector('#passkey-prompt-modal');
                if (modal && !modal.__closeListenerAttached) {
                    console.log('[PasskeyPrompt] Attaching close event listener');
                    // Use arrow function to preserve 'this' context
                    modal.addEventListener('close', () => this.handleModalClose());
                    modal.__closeListenerAttached = true;
                }
            },

            onBeforeUnmount() {
                // Clean up event listener flag (listener will be garbage collected with component)
                const modal = this.refs?.modal || document.querySelector('#passkey-prompt-modal');
                if (modal) {
                    modal.__closeListenerAttached = false;
                }
            },

            /**
             * Handle modal close event (backdrop click, ESC key, or programmatic close)
             * This ensures the login flow continues even if user closes the modal accidentally
             */
            async handleModalClose() {
                console.log('[PasskeyPrompt] Modal closed detected');
                
                // If dismissal was already handled by button click, skip
                if (this._dismissHandled) {
                    console.log('[PasskeyPrompt] Dismissal already handled by button, skipping');
                    this._dismissHandled = false;
                    return;
                }
                
                // If we're not in a loading state, treat this as a dismissal
                // (Loading state means user clicked "Add Passkey" and we're processing)
                if (!this.state.loading) {
                    console.log('[PasskeyPrompt] Modal closed without action - treating as dismissal');
                    
                    // Mark as dismissed so we don't show again for a while
                    // Use stored currentUserUuid which is set when modal is shown
                    let userUuid = this.currentUserUuid;
                    
                    // If not set (e.g., when modal shown via DOM fallback), fetch it now
                    if (!userUuid) {
                        console.log('[PasskeyPrompt] currentUserUuid not set, fetching fresh user data...');
                        try {
                            const authService = await getAuthService();
                            const result = await authService.getCurrentUser({ skipLoading: true });
                            if (result.success && result.user) {
                                userUuid = result.user.userUuid;
                                this.currentUserUuid = userUuid;
                                console.log('[PasskeyPrompt] Got userUuid from API:', userUuid);
                            }
                        } catch (error) {
                            console.error('[PasskeyPrompt] Error fetching user in handleModalClose:', error);
                        }
                    }
                    
                    if (userUuid) {
                        const dismissedKey = `passkey-prompt-dismissed-${userUuid}`;
                        localStorage.setItem(dismissedKey, Date.now().toString());
                        console.log('[PasskeyPrompt] Marked as dismissed for user:', userUuid);
                    } else {
                        console.warn('[PasskeyPrompt] No userUuid available for dismissal');
                    }
                    
                    // Notify parent that user dismissed the prompt
                    if (this.props.onDismiss) {
                        this.props.onDismiss();
                    }
                }
            },

            /**
             * Show the passkey registration prompt
             * Call this method from parent component when appropriate
             */
            async show() {
                console.log('[PasskeyPrompt] show() called');
                console.log('[PasskeyPrompt] supported:', this.state.supported);
                
                if (!this.state.supported) {
                    console.log('[PasskeyPrompt] ❌ Passkeys not supported, skipping prompt');
                    return;
                }

                // Get fresh user data to ensure hasPasskey is current
                let lastUser = ReturningUserManager.getLastUser();
                console.log('[PasskeyPrompt] lastUser from cache:', lastUser);
                
                // Verify with live data from /auth/me
                try {
                    const authService = await getAuthService();
                    const result = await authService.getCurrentUser({ skipLoading: true });
                    if (result.success && result.user) {
                        console.log('[PasskeyPrompt] Fresh user data from /auth/me:', result.user);
                        // Use the fresh data which has up-to-date hasPasskey status
                        lastUser = {
                            userUuid: result.user.userUuid,
                            email: result.user.email,
                            name: result.user.name,
                            hasPasskey: result.user.hasPasskey,
                            hasPassword: result.user.hasPassword
                        };
                    }
                } catch (error) {
                    console.error('[PasskeyPrompt] Error fetching fresh user data:', error);
                }
                
                console.log('[PasskeyPrompt] lastUser.hasPasskey:', lastUser?.hasPasskey);
                
                if (lastUser?.hasPasskey) {
                    console.log('[PasskeyPrompt] ❌ User already has passkey, skipping prompt');
                    return;
                }

                // Check if user dismissed the prompt recently
                const dismissedKey = `passkey-prompt-dismissed-${lastUser?.userUuid || 'unknown'}`;
                const dismissed = localStorage.getItem(dismissedKey);
                console.log('[PasskeyPrompt] dismissed:', dismissed);
                
                if (dismissed) {
                    const dismissedAt = parseInt(dismissed, 10);
                    const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
                    console.log('[PasskeyPrompt] daysSinceDismissed:', daysSinceDismissed);
                    
                    // Don't show again for 7 days after dismissal
                    if (daysSinceDismissed < 7) {
                        console.log('[PasskeyPrompt] ❌ User dismissed prompt recently, skipping');
                        return;
                    }
                }

                console.log('[PasskeyPrompt] ✅ All checks passed, showing modal...');
                console.log('[PasskeyPrompt] refs.modal:', this.refs.modal);
                
                // Store the current user UUID for use in dismissal handlers
                if (lastUser?.userUuid) {
                    this.currentUserUuid = lastUser.userUuid;
                    console.log('[PasskeyPrompt] Stored currentUserUuid:', this.currentUserUuid);
                }
                
                // Ensure event listener is attached before showing
                this.attachEventListener();
                
                // Show the modal
                const modal = this.refs?.modal || document.querySelector('#passkey-prompt-modal');
                if (modal && typeof modal.showModal === 'function') {
                    modal.showModal();
                    console.log('[PasskeyPrompt] Modal showModal() called');
                }
            },

            /**
             * Hide the modal
             */
            hide() {
                if (this.refs && this.refs.modal) {
                    this.refs.modal.close();
                } else {
                    const dialogElement = document.querySelector('#passkey-prompt-modal');
                    if (dialogElement) {
                        dialogElement.close();
                    }
                }
            },

            /**
             * Switch the already-open prompt between modal and non-modal.
             *
             * A modal <dialog> (showModal) sits in the browser top layer and
             * inerts the rest of the page. The WebAuthn registration ceremony
             * is often handled by a password-manager extension (1Password,
             * Dashlane, ...) that injects its passkey UI *into the page* — and
             * while this dialog is modal, that injected UI is inert and can't
             * be clicked, so the user can never finish adding the passkey. We
             * drop to a non-modal dialog (show) for the ceremony so the
             * extension UI is interactive, then restore modal to present any
             * error consistently.
             *
             * Re-opening requires close() first; that fires the 'close' event,
             * but handleModalClose ignores it while state.loading is true
             * (set before this is called), so it is not treated as a dismissal.
             */
            setModalMode(modal) {
                const dlg = this.refs?.modal || document.querySelector('#passkey-prompt-modal');
                if (!dlg || typeof dlg.showModal !== 'function') return;
                if (dlg.open) dlg.close();
                if (modal) {
                    dlg.showModal();
                } else {
                    dlg.show();
                }
            },

            async handleAddPasskey() {
                // No nickname input needed - backend generates smart name from AAGUID
                const nickname = undefined;

                this.update({
                    loading: true,
                    error: null
                });

                // Drop to a non-modal dialog so a password-manager extension's
                // in-page passkey UI is clickable during the ceremony (a modal
                // dialog would inert it). See setModalMode().
                this.setModalMode(false);

                try {
                    // Register the passkey (nickname optional - backend auto-generates if not provided)
                    const PasskeyManager = await getPasskeyManager()
                    const result = await PasskeyManager.registerPasskey(nickname);

                    if (result.success) {
                        console.log('Passkey registered successfully');
                        
                        // Set flag to prevent double notification when modal closes
                        this._dismissHandled = true;
                        
                        // Hide the modal
                        this.hide();

                        // Notify parent of success
                        if (this.props.onSuccess) {
                            this.props.onSuccess({
                                credentialId: result.credentialId
                            });
                        }

                        // Update returning user info
                        const lastUser = ReturningUserManager.getLastUser();
                        if (lastUser) {
                            ReturningUserManager.saveUser({
                                ...lastUser,
                                hasPasskey: true
                            });
                        }
                    } else {
                        throw new Error(result.error || this.locale?.t('passkeyprompt_error_failed'));
                    }
                } catch (error) {
                    console.error('Passkey registration error:', error);

                    // Determine user-friendly error message
                    let errorMessage = error.message || this.locale?.t('passkeyprompt_error_unexpected');

                    if (error.name === 'NotAllowedError') {
                        errorMessage = this.locale?.t('passkeyprompt_error_cancelled');
                    } else if (error.name === 'InvalidStateError') {
                        errorMessage = this.locale?.t('passkeyprompt_error_exists');
                    } else if (error.name === 'SecurityError') {
                        errorMessage = this.locale?.t('passkeyprompt_error_security');
                    } else if (error.name === 'AbortError') {
                        errorMessage = this.locale?.t('passkeyprompt_error_aborted');
                    }

                    // Restore the modal (top-layer, dimmed) so the error alert
                    // is presented the same way the prompt was. Done while
                    // loading is still true so the close()/reopen isn't read as
                    // a dismissal by handleModalClose.
                    this.setModalMode(true);

                    this.update({
                        loading: false,
                        error: errorMessage
                    });

                    // Notify parent of error
                    if (this.props.onError) {
                        this.props.onError(error);
                    }
                } finally {
                    this.update({ loading: false });
                }
            },

            async handleMaybeLater() {
                console.log('[PasskeyPrompt] handleMaybeLater clicked');
                
                // Mark as dismissed so we don't show again for a while
                // Use stored currentUserUuid which is set when modal is shown
                let userUuid = this.currentUserUuid;
                
                // If not set (e.g., when modal shown via DOM fallback), fetch it now
                if (!userUuid) {
                    console.log('[PasskeyPrompt] currentUserUuid not set, fetching fresh user data...');
                    try {
                        const authService = await getAuthService();
                        const result = await authService.getCurrentUser({ skipLoading: true });
                        if (result.success && result.user) {
                            userUuid = result.user.userUuid;
                            this.currentUserUuid = userUuid;
                            console.log('[PasskeyPrompt] Got userUuid from API:', userUuid);
                        }
                    } catch (error) {
                        console.error('[PasskeyPrompt] Error fetching user in handleMaybeLater:', error);
                    }
                }
                
                if (userUuid) {
                    const dismissedKey = `passkey-prompt-dismissed-${userUuid}`;
                    localStorage.setItem(dismissedKey, Date.now().toString());
                    console.log('[PasskeyPrompt] Marked as dismissed for user:', userUuid);
                } else {
                    console.warn('[PasskeyPrompt] No userUuid available for dismissal');
                }

                // Close the modal - this will trigger handleModalClose event
                // but we set a flag to prevent double notification
                this._dismissHandled = true;
                this.hide();

                // Notify parent directly (handleModalClose won't notify since flag is set)
                if (this.props.onDismiss) {
                    this.props.onDismiss();
                }
            }
        }
    </script>

    <style>
        :host {
            display: contents;
        }
    </style>
</passkey-registration-prompt>